import React, { useEffect } from "react";
import { Card, Checkbox, Col, Form, Input, Row, Table, Tooltip, Typography } from "antd";
import i18n from "translations/i18n";
import { InfoCircleOutlined } from "@ant-design/icons";
import { formatPrice } from "functions/formatters";
import styled from "styled-components";
import EditablePriceInput from "../../../../../components/shared-components/EditablePriceInput";
import { useCountries } from "../../../../../data/hooks/firestore/useCountries";
import { usePriceLists } from "../../../../../data/hooks/firestore/usePriceLists";
import { NETHERLANDS_COUNTRY_CODE } from "../../../../../constants/PricingConstants";
import { CURRENCY } from "../../../../../constants/DeliveryConstants";

const { Text } = Typography;

const BundlePriceCalculator = ({ form, products, disabled }) => {
  const isBundle = Form.useWatch("isBundle", form);
  const bundleItems = Form.useWatch("bundleItems", { form, preserve: true }) || [];
  const packagingMaterial = Form.useWatch("packagingMaterial", form);
  const hasCustomPrice = Form.useWatch("hasCustomPrice", form);
  const customPrice = Form.useWatch("price", form);
  const { countries } = useCountries();
  const { priceLists } = usePriceLists();
  const standardPrice = parseFloat(customPrice || 0);

  // Same per-country override mechanism as the non-bundle product form (GeneralField.js):
  // NL drives the bundle's own price field, other countries are optional overrides relative to it.
  const handleCountryPriceChange = (countryCode, value) => {
    const fieldName = `countryPrice_${countryCode.toLowerCase()}`;

    if (!value || value === "") {
      form.setFieldsValue({ [fieldName]: null });
      return;
    }

    const newPrice = parseFloat(value);

    if (newPrice === standardPrice) {
      form.setFieldsValue({ [fieldName]: null });
    } else {
      form.setFieldsValue({ [fieldName]: newPrice });
    }
  };

  // NL is always shown - it's the bundle's own price field, not an override. Other countries
  // only show up once they already have a country price list; this form never creates one itself,
  // new countries must be added first via the Price Lists page.
  const priceTableData = countries
    .filter(
      (country) =>
        country.countryCode === NETHERLANDS_COUNTRY_CODE ||
        priceLists.some((pl) => pl.type === "country" && pl.countryCode === country.countryCode),
    )
    .map((country) => ({
      key: country.countryCode,
      countryCode: country.countryCode,
      isNetherlands: country.countryCode === NETHERLANDS_COUNTRY_CODE,
    }))
    // NL is the default/standard price, so it always comes first
    .sort((a, b) => (a.isNetherlands ? -1 : b.isNetherlands ? 1 : 0));

  const priceTableColumns = [
    {
      title: i18n.t("country"),
      dataIndex: "countryCode",
      key: "countryCode",
      render: (countryCode, record) => (
        <span>
          {countryCode}
          {record.isNetherlands && (
            <span style={{ marginLeft: 6, fontSize: "11px", color: "#888", fontWeight: "normal" }}>
              ({i18n.t("default")})
            </span>
          )}
        </span>
      ),
    },
    {
      title: i18n.t("price"),
      key: "price",
      width: 160,
      render: (_, record) => {
        const { countryCode, isNetherlands } = record;

        // NL drives the bundle price itself (the "price" field above), so it's shown read-only here.
        if (isNetherlands) {
          return (
            <span>
              {CURRENCY}
              {formatPrice(standardPrice)}
            </span>
          );
        }

        const fieldName = `countryPrice_${countryCode.toLowerCase()}`;

        return (
          <>
            <Form.Item name={fieldName} noStyle>
              <input type="hidden" />
            </Form.Item>
            <Form.Item shouldUpdate noStyle>
              {() => (
                <EditablePriceInput
                  record={{ id: countryCode, price: standardPrice }}
                  localCustomPrices={{ [countryCode]: form.getFieldValue(fieldName) }}
                  onPriceChange={(_, value) => handleCountryPriceChange(countryCode, value)}
                  disabled={disabled}
                />
              )}
            </Form.Item>
          </>
        );
      },
    },
  ];

  const getProductById = (productId) => {
    return products.find((p) => p.productId === productId);
  };

  // Calculate base price from bundle items + packaging
  const calculateBasePrice = () => {
    let total = 0;

    // Add bundle items price
    bundleItems.forEach((item) => {
      const product = getProductById(item.productId);
      if (product) {
        const itemPrice = product.price || 0;
        const itemTotal = itemPrice * item.quantity;
        total += itemTotal;
      }
    });

    // Add packaging price
    if (packagingMaterial?.productId) {
      const packaging = getProductById(packagingMaterial.productId);
      if (packaging) {
        const packagingPrice = packaging.price || 0;
        total += packagingPrice;
      }
    }

    return total;
  };

  const calculatePackagingPrice = () => {
    if (!packagingMaterial?.productId) return 0;
    const packaging = getProductById(packagingMaterial.productId);
    if (!packaging) return 0;
    return packaging.price || 0;
  };

  // Calculate item prices based on ratio when custom price is set
  const calculateItemPrices = () => {
    const result = {};

    if (!hasCustomPrice || !customPrice || bundleItems.length === 0) {
      // No custom price - use original prices
      bundleItems.forEach((item) => {
        const product = getProductById(item.productId);
        if (product) {
          result[item.productId] = product.price || 0;
        }
      });
      return result;
    }

    // Calculate total price of products only (excl. packaging)
    let totalProductsPrice = 0;
    bundleItems.forEach((item) => {
      const product = getProductById(item.productId);
      if (product) {
        const itemPrice = product.price || 0;
        totalProductsPrice += itemPrice * item.quantity;
      }
    });

    if (totalProductsPrice === 0) return result;

    const packagingPrice = calculatePackagingPrice();
    const targetProductsPrice = parseFloat(customPrice) - packagingPrice;
    const priceDifference = targetProductsPrice - totalProductsPrice;

    // Calculate new price for each product based on ratio
    bundleItems.forEach((item) => {
      const product = getProductById(item.productId);
      if (product) {
        const originalPrice = product.price || 0;
        const ratio = (originalPrice * item.quantity) / totalProductsPrice;
        const adjustment = priceDifference * ratio;
        const newPricePerItem = originalPrice + adjustment / item.quantity;

        // Store with full precision (up to 16 decimals)
        result[item.productId] = newPricePerItem;
      }
    });

    return result;
  };

  // Auto-update price when bundle changes and custom price is disabled
  useEffect(() => {
    // 👇 Skip if hasCustomPrice is still loading
    if (hasCustomPrice === undefined) {
      return;
    }

    if (isBundle && !hasCustomPrice) {
      const basePrice = calculateBasePrice();
      form.setFieldsValue({ price: parseFloat(basePrice.toFixed(2)) });
    }
  }, [hasCustomPrice]);

  // Calculate and store item prices whenever relevant fields change
  useEffect(() => {
    if (isBundle) {
      const calculatedPrices = calculateItemPrices();
      form.setFieldsValue({ calculatedItemPrices: calculatedPrices });
    }
  }, [isBundle, bundleItems, packagingMaterial, hasCustomPrice, customPrice]);

  // Reset custom price when toggled off
  useEffect(() => {
    // 👇 NIEUWE CHECK: Skip if hasCustomPrice is still loading (undefined)
    if (hasCustomPrice === undefined) {
      return;
    }

    if (isBundle && !hasCustomPrice) {
      const basePrice = calculateBasePrice();
      form.setFieldsValue({ price: parseFloat(basePrice.toFixed(2)) });
    }
  }, [bundleItems, packagingMaterial, hasCustomPrice, isBundle]);

  if (!isBundle) return null;

  const basePrice = calculateBasePrice();
  const packagingPrice = calculatePackagingPrice();
  const totalItems = bundleItems.reduce((sum, item) => sum + (item.quantity || 0), 0);
  const calculatedPrices = calculateItemPrices();

  return (
    <Card title={i18n.t("bundle_pricing")}>
      {/* Hidden field to store calculated prices */}
      <Form.Item name="calculatedItemPrices" hidden>
        <Input />
      </Form.Item>

      <Row gutter={16}>
        <Col xs={24}>
          <Form.Item name="hasCustomPrice" valuePropName="checked">
            <Checkbox disabled={disabled}>{i18n.t("use_custom_price")}</Checkbox>
          </Form.Item>
        </Col>

        <Col xs={24} md={12}>
          <Form.Item
            name="price"
            label={i18n.t("bundle_price")}
            tooltip={{
              title: hasCustomPrice ? i18n.t("form.custom_price_tooltip") : i18n.t("form.calculated_price_tooltip"),
              icon: <InfoCircleOutlined />,
            }}
            rules={[
              {
                required: true,
                message: i18n.t("form.enter_price"),
              },
            ]}
          >
            <Input
              type="number"
              step="0.01"
              placeholder={i18n.t("price")}
              className="w-100"
              disabled={!hasCustomPrice || disabled}
            />
          </Form.Item>
        </Col>

        <Col xs={24} md={12}>
          <PriceInfoBox>
            {!hasCustomPrice && (
              <InfoRow>
                <Text type="secondary">{i18n.t("calculated_base_price")}:</Text>
                <Text strong>€{formatPrice(basePrice)}</Text>
              </InfoRow>
            )}

            {hasCustomPrice && (
              <InfoRow>
                <Text type="secondary">{i18n.t("custom_price")}:</Text>
                <Text strong>€{formatPrice(customPrice)}</Text>
              </InfoRow>
            )}

            {/* Show individual product prices */}
            {bundleItems.map((item) => {
              const product = getProductById(item.productId);
              const calculatedPrice = calculatedPrices[item.productId];
              if (!product || calculatedPrice === undefined) return null;

              return (
                <InfoRow key={item.productId}>
                  <Text type="secondary">{product.productName}:</Text>
                  <Text strong>€{formatPrice(calculatedPrice)}</Text>
                </InfoRow>
              );
            })}

            <InfoRow>
              <Text type="secondary">{i18n.t("price_packaging")}:</Text>
              <Text strong>€{formatPrice(packagingPrice)}</Text>
            </InfoRow>

            <InfoRow>
              <Text type="secondary">{i18n.t("total_items_in_bundle")}:</Text>
              <Text strong>{totalItems}</Text>
            </InfoRow>
          </PriceInfoBox>
        </Col>
      </Row>

      {hasCustomPrice && customPrice && parseFloat(customPrice) - packagingPrice < 0 && (
        <WarningBox>
          <InfoCircleOutlined style={{ marginRight: 8 }} />
          {i18n.t("warning_negative_item_price")}
        </WarningBox>
      )}

      <Row gutter={16} style={{ marginTop: 24 }}>
        <Col xs={24}>
          <PriceSectionTitle>
            {i18n.t("price_per_country")}{" "}
            <Tooltip title={i18n.t("form.price_per_country_tooltip")}>
              <InfoCircleOutlined style={{ fontSize: 13, color: "#888" }} />
            </Tooltip>
          </PriceSectionTitle>
          <Table columns={priceTableColumns} dataSource={priceTableData} pagination={false} size="small" />
        </Col>
      </Row>
    </Card>
  );
};

const PriceInfoBox = styled.div`
  padding: 16px;
  background: #f5f5f5;
  border-radius: 8px;
  margin-top: 30px;
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const PriceSectionTitle = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.85);
  margin-bottom: 16px;
`;

const WarningBox = styled.div`
  margin-top: 16px;
  padding: 12px;
  background: #fff7e6;
  border: 1px solid #ffd591;
  border-radius: 8px;
  color: #d46b08;
`;

export default BundlePriceCalculator;
